import { CompanyTypeSlug } from 'models/companies'

/**
 * Pure decision helpers for the "Push to Campaign" / "Unassign from Campaign"
 * bulk action that every Data Expansion table (DECommonTable) shares.
 *
 * Extracted so the parity rule is explicit and unit-testable: any non-doors
 * company-type table (restaurants, universities/C&U, K-12, hospitals, nursing
 * homes, ...) surfaces the campaign action identically. This guards against
 * a regression like FIR-6635, where the Colleges & Universities ("education-cu")
 * table was reported as missing the campaign option that other tables have.
 *
 * Visibility depends only on the global `enableCampaigns` feature flag, the
 * per-table `campaigns` feature opt-out, and whether the table is a doors
 * table. It is intentionally NOT gated on the company type, so adding a new
 * non-doors table gets campaign assignment for free.
 */

export interface CampaignTableActionGate {
  /** Global `enable_campaigns` feature flag. */
  enableCampaigns: boolean
  /** Whether this table renders doors rather than companies. */
  isDoorsTable?: boolean
  /**
   * Per-table opt-out: `tableFeatures.campaigns` resolved via the table's
   * `getFeatureEnabled('campaigns')` (defaults to enabled when unset).
   */
  campaignsFeatureEnabled: boolean
  /** The company type the table renders (for documentation / future gating). */
  companyTypeSlug?: CompanyTypeSlug
}

/**
 * Whether the campaign bulk action should be rendered for this table at all.
 * True for any non-doors table when campaigns are enabled globally and not
 * opted out for the specific table.
 */
export function shouldShowCampaignAction(
  gate: CampaignTableActionGate
): boolean {
  return Boolean(
    gate.enableCampaigns && !gate.isDoorsTable && gate.campaignsFeatureEnabled
  )
}

export type CampaignActionKind = 'push' | 'unassign'

/**
 * Which campaign action to show: "unassign" when the table is already scoped to
 * a campaign (campaign targets view), otherwise "push" (assign to a campaign).
 */
export function getCampaignActionKind(
  hasCampaign: boolean
): CampaignActionKind {
  return hasCampaign ? 'unassign' : 'push'
}
